import * as React from 'react';
import { WorkspaceButton } from '@/components/ui/workspace';
import { CLIENT_NAME_REMOVED } from '@/components/ui/citation-chip';
import { errorMessage, usePatchProposal, type KbProposal } from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import { Actions, FormError, InlineForm } from '../kb-bits';
import { narrowRange, plural, quoteTokens } from '../kb-logic';

/**
 * Narrow a proposal's quote (E13-F06 §3, `PATCH /items/{id}` `narrow`):
 * the curator picks the first and the last word to keep, inside the
 * passage the proposal quotes. Words can only be left out; a removed
 * client name stays whole. The server re-checks the narrowed quote against
 * the capture (rule 7) before it is stored.
 */
export function NarrowQuote({
  proposal,
  onDone,
  onCancel,
}: {
  proposal: KbProposal;
  onDone: () => void;
  onCancel: () => void;
}) {
  const tokens = React.useMemo(
    () => quoteTokens(proposal.excerpt_text, proposal.redactions),
    [proposal.excerpt_text, proposal.redactions],
  );
  const [from, setFrom] = React.useState<number | null>(null);
  const [to, setTo] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const patch = usePatchProposal();

  const lo = from === null ? null : to === null ? from : Math.min(from, to);
  const hi = from === null ? null : to === null ? from : Math.max(from, to);
  const kept = lo === null || hi === null ? 0 : hi - lo + 1;

  const pick = (k: number) => {
    setError(null);
    if (from === null || to !== null) {
      setFrom(k);
      setTo(null);
    } else {
      setTo(k);
    }
  };

  const apply = () => {
    if (lo === null || hi === null) return;
    const range = narrowRange(tokens, proposal.excerpt_start, lo, hi);
    if (!range) return;
    patch.mutate(
      { id: proposal.id, body: { narrow: range } },
      { onSuccess: onDone, onError: (err) => setError(errorMessage(err)) },
    );
  };

  return (
    <InlineForm aria-label="Narrow the quote">
      <div className="font-semibold text-ink-900">Narrow the quote</div>
      <div className="text-meta text-ink-500">
        Pick the first and the last word to keep. Words can only be left out; nothing else in the
        quote can change.
      </div>
      <div className="mt-2.5 font-serif text-[15px] leading-[30px] text-ink-900">
        {tokens.map((t, k) => {
          const inRange = lo !== null && hi !== null && k >= lo && k <= hi;
          const outside = lo !== null && hi !== null && to !== null && !inRange;
          return (
            <React.Fragment key={k}>
              <button
                type="button"
                aria-pressed={inRange}
                aria-label={t.removed ? CLIENT_NAME_REMOVED : undefined}
                onClick={() => pick(k)}
                className={cn(
                  'rounded border-0 bg-transparent px-px font-[inherit] text-[inherit] hover:bg-paper-100',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600',
                  inRange && 'bg-cite-highlight hover:bg-cite-highlight',
                  outside && 'text-ink-300 line-through decoration-ink-300',
                  t.removed && 'font-sans text-meta font-medium',
                )}
              >
                {t.text}
              </button>{' '}
            </React.Fragment>
          );
        })}
      </div>
      {error ? <FormError>{error}</FormError> : null}
      <Actions>
        <WorkspaceButton
          variant="primary"
          disabled={kept === 0 || to === null || patch.isPending}
          onClick={apply}
        >
          {to === null ? 'Pick the last word' : `Keep ${plural(kept, 'word')}`}
        </WorkspaceButton>
        <WorkspaceButton variant="tertiary" onClick={onCancel}>
          Cancel
        </WorkspaceButton>
      </Actions>
    </InlineForm>
  );
}
